import { defineStore } from 'pinia'

import { storage } from 'src/lib/storage.js'

// one key per account on this browser (the drafts of before had one for
// all: the first account that loads takes them over)
const SHARED_KEY = 'pleroma-fe-drafts'
const storageKey = (owner) => (owner ? `${SHARED_KEY}:${owner}` : SHARED_KEY)

/*
 * Note: we do not use the persist state plugin because
 * it is not impossible for a user to have two windows at
 * the same time. The persist state plugin is just overriding
 * everything with the current state. This isn't good because
 * if a draft is created in one window and another draft is
 * created in another, the draft in the first window will just
 * be overriden.
 * Here, we can't guarantee 100% atomicity unless one uses
 * different keys, which will just pollute the whole storage.
 * It is indeed best to have backend support for this.
 */
const getStorageData = async (owner) => await storage.getItem(storageKey(owner))

// Order entire read/modify/write operations within this tab, per account.
// A failed write must not prevent the next save or deletion from running.
const storageWrites = new Map()
const updateStorage = (owner, update) => {
  if (!owner) return Promise.resolve()
  const previous = storageWrites.get(owner) ?? Promise.resolve()
  const next = previous
    .catch(() => undefined)
    .then(async () => {
      const currentData = (await getStorageData(owner)) ?? {}
      await storage.setItem(storageKey(owner), update(currentData))
    })
  const tracked = next.finally(() => {
    if (storageWrites.get(owner) === tracked) storageWrites.delete(owner)
  })
  storageWrites.set(owner, tracked)
  return tracked
}

// no account (logged out): nothing is written, not even to the key of
// before, which the next account would take over
const saveDraftToStorage = (owner, draft) => {
  if (!owner) return Promise.resolve()
  const snapshot = JSON.parse(JSON.stringify(draft))
  return updateStorage(owner, (currentData) => ({
    ...currentData,
    [snapshot.id]: snapshot,
  }))
}

const deleteDraftFromStorage = (owner, ids) =>
  updateStorage(owner, (currentData) => {
    const remaining = { ...currentData }
    ids.forEach((id) => {
      delete remaining[id]
    })
    return remaining
  })

export const useDraftsStore = defineStore('drafts', {
  state: () => ({
    drafts: new Map(),
    // the account whose drafts these are
    owner: null,
  }),
  getters: {
    draftsArray(state) {
      return [...state.drafts.values()]
    },
    draftsCount(state) {
      return state.drafts.size
    },
  },
  actions: {
    async loadDrafts(owner = null) {
      this.owner = owner
      let currentData = await getStorageData(owner)
      const shared = owner && !currentData && (await getStorageData(null))
      if (shared && Object.keys(shared).length) {
        currentData = shared
        await storage.setItem(storageKey(owner), shared)
        await storage.removeItem(SHARED_KEY)
      }
      this.drafts = new Map(Object.entries(currentData ?? {}))
    },
    newDraftId() {
      let timestamp = Date.now()
      while (this.drafts.has(timestamp.toString())) timestamp++
      return timestamp.toString()
    },
    async addOrSaveDraft(draft) {
      const id = draft.id ?? this.newDraftId()
      const draftWithId = { ...draft, id }
      this.drafts.set(id, draftWithId)
      await saveDraftToStorage(this.owner, draftWithId)
      return id
    },
    async abandonDraft(id) {
      this.drafts.delete(id)
      await deleteDraftFromStorage(this.owner, [id])
    },
    async abandonAllDrafts() {
      const ids = [...this.drafts.keys()]
      ids.forEach((id) => this.drafts.delete(id))
      await deleteDraftFromStorage(this.owner, ids)
    },
  },
})
